import railsPostFetch from '@aytm/lib/common/rails-post-fetch'
import CSRFToken from '@aytm/lib/csrf-token'
import { Storage } from '@aytm/lib/storage'
import { transformClusterCoords } from '@aytm/app/stats/page/question-views/hm/components/controls/recognition-form/lib/utils'
import { deleteSurveyAsset, getSurveyAssets, updateSurveyAsset } from '@aytm/app/stats/lib/stat-page'
import { HM_CLUSTER } from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// @ts-ignore
const STAT_SERVER_URL = PageGlobals.STAT_SERVER_URL || ''
const STAT_URL_HM = `${STAT_SERVER_URL}/assets/hm.cluster`
const ANNOTATION_URL = '/image_assets/annotate'

const getUrlWithParams = async (keyCode, visualPosition) => {
  const config = { qid: 'q' + visualPosition }
  const base64zip = await Storage.Encode(config)
  const params = new URLSearchParams({ key: keyCode, config: base64zip }).toString()
  return params
}

// A CRUD key can be refused even while the cache believes it current: the expiry check is a guess
// about the server's clock, and only the server can say a key is spent. The refusal is carried in
// the answer's own body rather than its status line, so the body is what decides, and it is read on
// the refused path as well as the accepted one.
const KEY_REFUSED = 'INVALID KEY'

export class CrudKeyRefusedError extends Error {
  constructor() {
    super(KEY_REFUSED)
    this.name = 'CrudKeyRefusedError'
  }
}

const isKeyRefused = payload =>
  KEY_REFUSED === payload?.status || KEY_REFUSED === payload?.error || KEY_REFUSED === payload?.message

// Reads a mutation's answer, raising a refused key as its own error so a caller can tell it from an
// ordinary failure and re-issue, rather than reporting both as the same thing.
const resolveMutation = async (response, failureMessage) => {
  const payload = await response.json().catch(() => ({}))

  if (isKeyRefused(payload)) throw new CrudKeyRefusedError()
  if (!response.ok) throw new Error(payload?.error || failureMessage)

  return payload
}

// The key is asked for through the host, the way every other surface on the page asks for one: the
// block hands the request to the bridge, which answers from its own cache or from the server.
// Holding a second cache here would be two answers to one question, and they can disagree.
const askForKey = (onFetchCRUDKey, forgetRefused = false) =>
  new Promise(resolve => {
    if ('function' != typeof onFetchCRUDKey) resolve(null)
    else onFetchCRUDKey(resolve, forgetRefused)
  })

// Runs the mutation with a key, and on a refusal asks for another and sends it once more. Once,
// deliberately: a key the server keeps refusing has to surface as an error rather than become a
// loop. The second ask says the key was refused, so the holder drops it rather than handing it back.
export const withKeyReissue = async ({ onFetchCRUDKey }, action) => {
  const key = await askForKey(onFetchCRUDKey)
  if (!key?.code || !key?.expire) return { acquired: false, key }

  try {
    await action(key)
    return { acquired: true, reissued: false }
  } catch (error) {
    if (!(error instanceof CrudKeyRefusedError)) throw error
  }

  const fresh = await askForKey(onFetchCRUDKey, true)
  if (!fresh?.code || !fresh?.expire) return { acquired: false, key: fresh }

  await action(fresh)
  return { acquired: true, reissued: true }
}

// Clusters
export const createCluster = async ({ keyCode, clusterData, visualPosition }) => {
  const params = await getUrlWithParams(keyCode, visualPosition)
  const url = `${STAT_URL_HM}/create?${params}`

  const response = await railsPostFetch(url, {
    body: JSON.stringify(clusterData),
    credentials: 'omit',
    mode: 'cors'
  })

  return resolveMutation(response, 'Failed to create cluster')
}

export const updateCluster = async ({ keyCode, clusterData, visualPosition }) => {
  const params = await getUrlWithParams(keyCode, visualPosition)
  const url = `${STAT_URL_HM}/${clusterData.uid}/update?${params}`

  const response = await railsPostFetch(url, {
    body: JSON.stringify(clusterData),
    credentials: 'omit',
    mode: 'cors'
  })

  return resolveMutation(response, 'Failed to update cluster')
}

export const deleteCluster = async ({ keyCode, uid, visualPosition }) => {
  const params = await getUrlWithParams(keyCode, visualPosition)
  const url = `${STAT_URL_HM}/${uid}/delete?${params}`

  const response = await railsPostFetch(url, {
    mode: 'cors',
    credentials: 'omit',
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  })

  return resolveMutation(response, 'Failed to delete cluster')
}

// Recognition
export const fetchAnnotation = ({ imageId, type, isStart, signal }) => {
  const [param, token] = CSRFToken()
  const formData = new URLSearchParams({
    uid: imageId,
    type,
    start: isStart ? '1' : '0',
    [param]: token
  })

  return railsPostFetch(ANNOTATION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData.toString(),
    signal
  }).then(response => {
    if (!response.ok) throw new Error('Network response was not ok')
    return response.json()
  })
}

export const saveClustersToServer = async ({ keyCode, clusters, imageHeight, visualPosition }) => {
  const params = await getUrlWithParams(keyCode, visualPosition)

  const transformedClusters = transformClusterCoords(clusters, imageHeight)

  const response = await railsPostFetch(`${STAT_URL_HM}/create-bulk?${params}`, {
    mode: 'cors',
    credentials: 'omit',
    body: JSON.stringify({ clusters: transformedClusters })
  })

  return resolveMutation(response, 'Failed to create clusters')
}

export const syncSurveyAssets = (clustersSaved, visualPosition) => {
  const assets = getSurveyAssets(HM_CLUSTER)
  const assetsClusters = assets.filter(ac => ac.qid === `q${visualPosition}`)

  assetsClusters.forEach(assetsCluster => {
    const replacedCluster = clustersSaved.find(c => c.hash === assetsCluster.hash)
    if (assetsCluster.hash && replacedCluster) {
      deleteSurveyAsset(HM_CLUSTER, assetsCluster.uid)
    }
  })

  updateSurveyAsset(HM_CLUSTER, clustersSaved)
}
